import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  getAllApplications, updateApplication, deleteApplication,
  getAllBuyerApplications, updateBuyerApplication, deleteBuyerApplication,
  getAllVCPartnerApplications, updateVCPartnerApplication, deleteVCPartnerApplication,
  getAllPodcastSubscribers, updatePodcastSubscriber, deletePodcastSubscriber,
  resendToAirtable
} from '../services/api';
import { Trash2, RefreshCw, Send } from 'lucide-react';

// Editable Cell Component
const EditableCell = ({ value, onSave, type = 'text', options = [] }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (type === 'text') {
        inputRef.current.select();
      }
    }
  }, [isEditing, type]);

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  const handleSave = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value || '');
      setIsEditing(false);
    }
  };

  if (isEditing) {
    if (type === 'select') {
      return (
        <select
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="w-full bg-slate-700 border border-blue-500 rounded px-2 py-1 text-white text-sm focus:outline-none"
        >
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className="w-full bg-slate-700 border border-blue-500 rounded px-2 py-1 text-white text-sm focus:outline-none"
      />
    );
  }

  return (
    <div 
      onClick={() => setIsEditing(true)}
      className="cursor-pointer hover:bg-slate-700/50 rounded px-2 py-1 min-h-[28px] flex items-center"
    >
      {value || <span className="text-slate-500 italic">-</span>}
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusStyles = {
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
    approved: 'bg-green-500/20 text-green-400 border-green-500/50',
    rejected: 'bg-red-500/20 text-red-400 border-red-500/50',
  };
  
  return (
    <Badge className={`${statusStyles[status] || statusStyles.pending} text-xs`}>
      {status || 'pending'}
    </Badge>
  );
};

const AdminApplications = () => {
  const [activeTab, setActiveTab] = useState('founders');
  const [founderApps, setFounderApps] = useState([]);
  const [buyerApps, setBuyerApps] = useState([]);
  const [vcApps, setVCApps] = useState([]);
  const [podcastSubs, setPodcastSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [sending, setSending] = useState(null);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [founders, buyers, vcs, podcast] = await Promise.all([
        getAllApplications(),
        getAllBuyerApplications(),
        getAllVCPartnerApplications(),
        getAllPodcastSubscribers()
      ]);
      setFounderApps(founders);
      setBuyerApps(buyers);
      setVCApps(vcs);
      setPodcastSubs(podcast);
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendToAirtable = async (id, formType) => {
    setSending(id);
    try {
      await resendToAirtable(id, formType);
      alert('Successfully sent to Airtable!');
    } catch (error) {
      console.error('Error sending to Airtable:', error);
      alert('Failed to send to Airtable. Please try again.');
    } finally {
      setSending(null);
    }
  };

  const handleUpdateFounder = async (id, field, value) => {
    setSaving(id);
    try {
      await updateApplication(id, { [field]: value });
      setFounderApps(apps => apps.map(app => 
        app._id === id ? { ...app, [field]: value } : app
      ));
    } catch (error) {
      console.error('Error updating application:', error);
    } finally {
      setSaving(null);
    }
  };

  const handleDeleteFounder = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      await deleteApplication(id);
      setFounderApps(apps => apps.filter(app => app._id !== id));
    } catch (error) {
      console.error('Error deleting application:', error);
    }
  };

  const handleUpdateBuyer = async (id, field, value) => {
    setSaving(id);
    try {
      await updateBuyerApplication(id, { [field]: value });
      setBuyerApps(apps => apps.map(app => 
        app._id === id ? { ...app, [field]: value } : app
      ));
    } catch (error) {
      console.error('Error updating application:', error);
    } finally {
      setSaving(null);
    }
  };

  const handleDeleteBuyer = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      await deleteBuyerApplication(id);
      setBuyerApps(apps => apps.filter(app => app._id !== id));
    } catch (error) {
      console.error('Error deleting application:', error);
    }
  };

  const handleUpdateVC = async (id, field, value) => {
    setSaving(id);
    try {
      await updateVCPartnerApplication(id, { [field]: value });
      setVCApps(apps => apps.map(app => 
        app._id === id ? { ...app, [field]: value } : app
      ));
    } catch (error) {
      console.error('Error updating application:', error);
    } finally {
      setSaving(null);
    }
  };

  const handleDeleteVC = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      await deleteVCPartnerApplication(id);
      setVCApps(apps => apps.filter(app => app._id !== id));
    } catch (error) {
      console.error('Error deleting application:', error);
    }
  };

  const handleUpdatePodcast = async (id, field, value) => {
    setSaving(id);
    try {
      await updatePodcastSubscriber(id, { [field]: value });
      setPodcastSubs(subs => subs.map(sub => 
        sub._id === id ? { ...sub, [field]: value } : sub
      ));
    } catch (error) {
      console.error('Error updating subscriber:', error);
    } finally {
      setSaving(null);
    }
  };

  const handleDeletePodcast = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subscriber?')) return;
    try {
      await deletePodcastSubscriber(id);
      setPodcastSubs(subs => subs.filter(sub => sub._id !== id));
    } catch (error) {
      console.error('Error deleting subscriber:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabs = [
    { id: 'founders', label: 'Apply Now', count: founderApps.length },
    { id: 'buyers', label: 'Want to be a Judge', count: buyerApps.length },
    { id: 'vcs', label: 'Become a Referral Partner', count: vcApps.length },
    { id: 'podcast', label: 'Podcast Subscribers', count: podcastSubs.length },
  ];

  const statusOptions = ['pending', 'approved', 'rejected'];

  return (
    <AdminLayout currentPage="applications">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white">Applications</h2>
            <p className="text-slate-400 text-sm mt-1">
              View and manage all incoming applications. Click any field to edit.
            </p>
          </div>
          <Button
            onClick={loadAllData}
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-800">
          <div className="flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-slate-400 hover:text-white hover:border-slate-600'
                }`}
              >
                {tab.label}
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-blue-500/20' : 'bg-slate-800'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Spreadsheet Content */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading applications...</div>
          ) : (
            <div className="overflow-x-auto">
              {/* Founders Table */}
              {activeTab === 'founders' && (
                <table className="w-full text-sm">
                  <thead className="bg-slate-800/50 text-left">
                    <tr>
                      <th className="px-4 py-3 text-slate-300 font-medium">Full Name</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Email</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Category</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Status</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Form Type</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Applied</th>
                      <th className="px-4 py-3 text-slate-300 font-medium w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {founderApps.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                          No founder applications yet
                        </td>
                      </tr>
                    ) : (
                      founderApps.map((app) => (
                        <tr key={app._id} className={`hover:bg-slate-800/30 ${saving === app._id || sending === app._id ? 'opacity-50' : ''}`}>
                          <td className="px-4 py-2">
                            <EditableCell 
                              value={app.full_name} 
                              onSave={(val) => handleUpdateFounder(app._id, 'full_name', val)} 
                            />
                          </td>
                          <td className="px-4 py-2">
                            <EditableCell 
                              value={app.email} 
                              onSave={(val) => handleUpdateFounder(app._id, 'email', val)} 
                            />
                          </td>
                          <td className="px-4 py-2">
                            <EditableCell 
                              value={app.category} 
                              onSave={(val) => handleUpdateFounder(app._id, 'category', val)} 
                            />
                          </td>
                          <td className="px-4 py-2">
                            <EditableCell 
                              value={app.status || 'pending'} 
                              type="select"
                              options={statusOptions}
                              onSave={(val) => handleUpdateFounder(app._id, 'status', val)} 
                            />
                          </td>
                          <td className="px-4 py-2">
                            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50 text-xs">
                              {app.form_type || 'Apply Now'}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 text-slate-400 text-xs">
                            {formatDate(app.created_at)}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleResendToAirtable(app._id, app.form_type || 'Apply Now')}
                                disabled={sending === app._id}
                                className="p-1.5 text-slate-500 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors"
                                title="Resend to Airtable"
                              >
                                <Send className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteFounder(app._id)}
                                className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}

              {/* Buyers Table */}
              {activeTab === 'buyers' && (
                <table className="w-full text-sm">
                  <thead className="bg-slate-800/50 text-left">
                    <tr>
                      <th className="px-4 py-3 text-slate-300 font-medium">First Name</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Last Name</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Email</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Buyer Type</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Status</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Form Type</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Applied</th>
                      <th className="px-4 py-3 text-slate-300 font-medium w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {buyerApps.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                          No buyer applications yet
                        </td>
                      </tr>
                    ) : (
                      buyerApps.map((app) => (
                        <tr key={app._id} className={`hover:bg-slate-800/30 ${saving === app._id || sending === app._id ? 'opacity-50' : ''}`}>
                          <td className="px-4 py-2">
                            <EditableCell 
                              value={app.first_name} 
                              onSave={(val) => handleUpdateBuyer(app._id, 'first_name', val)} 
                            />
                          </td>
                          <td className="px-4 py-2">
                            <EditableCell 
                              value={app.last_name} 
                              onSave={(val) => handleUpdateBuyer(app._id, 'last_name', val)} 
                            />
                          </td>
                          <td className="px-4 py-2">
                            <EditableCell 
                              value={app.email} 
                              onSave={(val) => handleUpdateBuyer(app._id, 'email', val)} 
                            />
                          </td>
                          <td className="px-4 py-2">
                            <EditableCell 
                              value={app.buyer_type} 
                              type="select"
                              options={['Cybersecurity', 'Developer Tools', 'Finance Tech', 'HR Tech', 'Other']}
                              onSave={(val) => handleUpdateBuyer(app._id, 'buyer_type', val)} 
                            />
                          </td>
                          <td className="px-4 py-2">
                            <EditableCell 
                              value={app.status || 'pending'} 
                              type="select"
                              options={statusOptions}
                              onSave={(val) => handleUpdateBuyer(app._id, 'status', val)} 
                            />
                          </td>
                          <td className="px-4 py-2">
                            <Badge className="bg-green-500/20 text-green-400 border-green-500/50 text-xs">
                              {app.form_type || 'Judge / Buyer'}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 text-slate-400 text-xs">
                            {formatDate(app.created_at)}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleResendToAirtable(app._id, app.form_type || 'Judge / Buyer')}
                                disabled={sending === app._id}
                                className="p-1.5 text-slate-500 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors"
                                title="Resend to Airtable"
                              >
                                <Send className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteBuyer(app._id)}
                                className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}

              {/* VC Partners Table */}
              {activeTab === 'vcs' && (
                <table className="w-full text-sm">
                  <thead className="bg-slate-800/50 text-left">
                    <tr>
                      <th className="px-4 py-3 text-slate-300 font-medium">First Name</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Last Name</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Email</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Firm Name</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Role</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Status</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Form Type</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Applied</th>
                      <th className="px-4 py-3 text-slate-300 font-medium w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {vcApps.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                          No VC partner applications yet
                        </td>
                      </tr>
                    ) : (
                      vcApps.map((app) => (
                        <tr key={app._id} className={`hover:bg-slate-800/30 ${saving === app._id || sending === app._id ? 'opacity-50' : ''}`}>
                          <td className="px-4 py-2">
                            <EditableCell 
                              value={app.first_name} 
                              onSave={(val) => handleUpdateVC(app._id, 'first_name', val)} 
                            />
                          </td>
                          <td className="px-4 py-2">
                            <EditableCell 
                              value={app.last_name} 
                              onSave={(val) => handleUpdateVC(app._id, 'last_name', val)} 
                            />
                          </td>
                          <td className="px-4 py-2">
                            <EditableCell 
                              value={app.email} 
                              onSave={(val) => handleUpdateVC(app._id, 'email', val)} 
                            />
                          </td>
                          <td className="px-4 py-2">
                            <EditableCell 
                              value={app.firm_name} 
                              onSave={(val) => handleUpdateVC(app._id, 'firm_name', val)} 
                            />
                          </td>
                          <td className="px-4 py-2">
                            <EditableCell 
                              value={app.role} 
                              type="select"
                              options={['Platform', 'Investor', 'Other']}
                              onSave={(val) => handleUpdateVC(app._id, 'role', val)} 
                            />
                          </td>
                          <td className="px-4 py-2">
                            <EditableCell 
                              value={app.status || 'pending'} 
                              type="select"
                              options={statusOptions}
                              onSave={(val) => handleUpdateVC(app._id, 'status', val)} 
                            />
                          </td>
                          <td className="px-4 py-2">
                            <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/50 text-xs">
                              {app.form_type || 'Referral Partner'}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 text-slate-400 text-xs">
                            {formatDate(app.created_at)}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleResendToAirtable(app._id, app.form_type || 'Referral Partner')}
                                disabled={sending === app._id}
                                className="p-1.5 text-slate-500 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors"
                                title="Resend to Airtable"
                              >
                                <Send className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteVC(app._id)}
                                className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}

              {/* Podcast Subscribers Table */}
              {activeTab === 'podcast' && (
                <table className="w-full text-sm">
                  <thead className="bg-slate-800/50 text-left">
                    <tr>
                      <th className="px-4 py-3 text-slate-300 font-medium">Email</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Form Type</th>
                      <th className="px-4 py-3 text-slate-300 font-medium">Subscribed</th>
                      <th className="px-4 py-3 text-slate-300 font-medium w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {podcastSubs.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                          No podcast subscribers yet
                        </td>
                      </tr>
                    ) : (
                      podcastSubs.map((sub) => (
                        <tr key={sub._id} className={`hover:bg-slate-800/30 ${saving === sub._id || sending === sub._id ? 'opacity-50' : ''}`}>
                          <td className="px-4 py-2">
                            <EditableCell 
                              value={sub.email} 
                              onSave={(val) => handleUpdatePodcast(sub._id, 'email', val)} 
                            />
                          </td>
                          <td className="px-4 py-2">
                            <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/50 text-xs">
                              {sub.form_type || 'Podcast Subscriber'}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 text-slate-400 text-xs">
                            {formatDate(sub.created_at)}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleResendToAirtable(sub._id, sub.form_type || 'Podcast Subscriber')}
                                disabled={sending === sub._id}
                                className="p-1.5 text-slate-500 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors"
                                title="Resend to Airtable"
                              >
                                <Send className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeletePodcast(sub._id)}
                                className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        <p className="text-slate-500 text-xs">
          Tip: Click any cell to edit it directly. Changes are saved automatically.
        </p>
      </div>
    </AdminLayout>
  );
};

export default AdminApplications;
